import type { ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { cn } from '@/utils/cn';
import { useAuthStore } from '@/stores/auth';
import {
  useCollectorSummary,
  useDailyEarnings,
  getMonday,
  weekStates,
} from '@/modules/payments';
import { pickRisk, type RiskState } from '@/utils/weekly-risk';
import type { AuthUser } from '@/types/auth';
import type { WeekStatesOpts } from '@/modules/payments';
import { useStreakEngineEnabled } from '@/hooks/use-streak-engine-enabled';

function weekOpts(user: AuthUser | null): WeekStatesOpts {
  return {
    joinedIso: user?.created?.slice(0, 10),
    streakResumedIso: user?.streak_resumed_at?.slice(0, 10),
    vacationFrom: user?.vacation_starts_at?.slice(0, 10),
    vacationTo: user?.vacation_ends_at?.slice(0, 10),
  };
}

const TONE: Record<RiskState['kind'], string> = {
  warn: 'border-brand-red/30 bg-brand-red/5 text-brand-red',
  grace: 'border-error/40 bg-error/10 text-error',
};

function WarnMessage({ daysLeft }: { daysLeft: number }): ReactNode {
  const label = daysLeft === 1 ? 'un día' : `${daysLeft} días`;
  return (
    <>
      Estás a solo {label} esta semana de no conseguir tu objetivo mínimo diario
      para <strong className="font-semibold uppercase">ser expulsado</strong>.
    </>
  );
}

function GraceMessage({ minDaily }: { minDaily: number }): ReactNode {
  return (
    <>
      Estás en periodo de gracia. Necesitas añadir al menos {minDaily} creadores
      hoy y mañana o serás expulsado.
    </>
  );
}

function BannerView({ state }: { state: RiskState }): ReactNode {
  return (
    <div
      role="status"
      className={cn(
        'flex items-start gap-sm rounded-lg border py-sm pl-base pr-base backdrop-blur-xl',
        TONE[state.kind]
      )}
    >
      <AlertTriangle size={16} className="mt-0.5 shrink-0" />
      <p className="flex-1 text-[11px] leading-relaxed sm:text-sm">
        {state.kind === 'warn' ? (
          <WarnMessage daysLeft={state.daysLeft} />
        ) : (
          <GraceMessage minDaily={state.minDaily} />
        )}
      </p>
    </div>
  );
}

function CollectorRiskBanner({ userId }: { userId: string }): ReactNode {
  const summary = useCollectorSummary(userId);
  const today = new Date().toISOString().slice(0, 10);
  const daily = useDailyEarnings(userId, getMonday(), today);
  const user = useAuthStore((s) => s.user);
  if (!summary.data) return null;
  // Fase 32: backend signals operational health and engine state separately.
  // Either one being off silences the risk banner — no point warning of a
  // ban when nothing will be evaluated.
  if (summary.data.system_health === 'error') return null;
  const minDaily = summary.data.daily_minimum_leads ?? 33;
  const days = weekStates(daily.data, minDaily, {
    ...weekOpts(user),
    omittedDates: summary.data.omitted_week_days,
  });
  const state = pickRisk({
    status: user?.account_status ?? '',
    days,
    minDaily,
  });
  if (!state) return null;
  return <BannerView state={state} />;
}

export function WeeklyRiskBanner(): ReactNode {
  const user = useAuthStore((s) => s.user);
  const engineEnabled = useStreakEngineEnabled();
  if (!user || user.role !== 'collector') return null;
  // Fase 28: redemption_test owns the messaging via /redemption screen.
  if (user.account_status === 'redemption_test') return null;
  if (!engineEnabled) return null;
  return <CollectorRiskBanner userId={user.id} />;
}
